import React, { useRef, useState } from 'react';
import {
    signInWithEmailAndPassword,
    signOut,
    signInWithPopup,
    OAuthProvider,
    sendEmailVerification,
    sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../firebase';
import type { User } from 'firebase/auth';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { initializeDashboardAccess, withTimeout } from '../DashboardAuth';
import { getSignupResumePath } from '../saas/SignupDraft';
import './Login.css';

const loginLogo = require('./assets/superpenguin-logo.webp');
const dashboardPreview = require('./assets/dashboard-corner-screenshot.png');
const penguinBackdrop = require('./assets/loginback.png');

// Firebase's production error omits the recovery guidance for temporary authentication throttling.
const signInThrottledMessage = 'Firebase has temporarily limited sign-in attempts. Pause before trying again. ' +
    'If you have forgotten your password, use “Forgot password?”.';

const Login: React.FC = () => {
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [loadingStage, setLoadingStage] = useState<'auth' | 'dashboard'>('auth');
    const [signupRecoveryPath, setSignupRecoveryPath] = useState<string | null>(null);
    // Block duplicate submit events before React has rendered the disabled controls.
    const authActionInProgress = useRef(false);

    const getUserEmail = (user: User | null): string | null => {
        if (!user) return null;
        if (user.email) {
            return user.email;
        }
        for (const info of user.providerData) {
            if (info?.email) {
                return info.email;
            }
        }
        return null;
    }

    const verifyTherapistAndSetClaims = async (user: User) => {
        // The callable resolves both UID-backed and random-id Microsoft Therapist records.
        const userEmail = getUserEmail(user);
        try {
            const access = await initializeDashboardAccess(user, userEmail);
            console.log('LOGIN: Dashboard access initialized:', access);
        } catch (claimsError) {
            // Never continue with a missing or stale server-owned access grant.
            console.error('LOGIN: Error initializing dashboard access:', claimsError);
            await withTimeout(signOut(auth), 5_000, 'Sign-out timed out.').catch((signOutError) =>
                console.warn('LOGIN: Could not fully close the rejected session:', signOutError)
            );
            const accessError = claimsError as { code?: string; details?: { reason?: string } };
            const verificationReason = accessError?.details?.reason;
            if (verificationReason === 'email_verification_required') {
                setSuccessMessage(null);
                throw new Error(
                    'Verify your email before signing in to the dashboard. ' +
                    'Use the verification email sent when the account was created, or ask your manager to resend it.'
                );
            }
            if (verificationReason === 'platform_authority_not_verified') {
                throw new Error(
                    'This platform account needs a one-time access migration. ' +
                    'Contact a Root administrator.'
                );
            }
            if (verificationReason === 'saas_service_locked') {
                throw new Error(
                    'Your organisation’s dashboard account is currently locked. ' +
                    'Contact support@superpenguin.com or phone your usual SuperPenguin contact to restore access. ' +
                    'Existing families keep access to the SuperPenguin app.'
                );
            }
            if (
                verificationReason === 'dashboard_access_not_found'
                || accessError?.code === 'functions/not-found'
                || accessError?.code === 'not-found'
            ) {
                setSignupRecoveryPath(getSignupResumePath(userEmail) || '/plans');
                throw new Error(
                    'Your account is verified, but your clinic setup is not complete. ' +
                    'Finish setting up your clinic before signing in to the dashboard.'
                );
            }
            if (claimsError instanceof Error && claimsError.message.startsWith('Access denied.')) {
                throw claimsError;
            }
            throw new Error('Could not initialise dashboard access. Please try signing in again.');
        }
    };

    // Retry delivery only after the server has admitted an unverified legacy user.
    // Newly created or SaaS users already have a server-issued link; sending a
    // second link here could invalidate the email that they already received.
    const requestPasswordEmailVerification = async (
        user: User
    ): Promise<'not_required' | 'sent' | 'failed'> => {
        if (user.emailVerified !== false) {
            return 'not_required';
        }

        const userEmail = getUserEmail(user) || email.trim();
        try {
            await sendEmailVerification(user, {
                url: new URL('/', window.location.origin).toString(),
                handleCodeInApp: false,
            });
            setSuccessMessage(
                `We sent a verification link to ${userEmail}. Open it, then return here and sign in again.`
            );
            return 'sent';
        } catch (verificationError: unknown) {
            // Delivery is best-effort for an admitted legacy account and must not revoke its current access.
            console.warn('LOGIN: Could not send the password-user verification email:', verificationError);
            return 'failed';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (authActionInProgress.current) return;
        authActionInProgress.current = true;
        setError(null);
        setSuccessMessage(null);
        setSignupRecoveryPath(null);
        setLoadingStage('auth');
        setIsLoading(true);
        
        try {
            const userCredential = await withTimeout(
                signInWithEmailAndPassword(auth, email.trim(), password),
                20_000,
                'Firebase sign-in timed out. Check your connection and try again.'
            );
            const user = userCredential.user;
            setLoadingStage('dashboard');
            await verifyTherapistAndSetClaims(user);
            await requestPasswordEmailVerification(user);
        } catch (error: any) {
            console.error('Error logging in', error);
            setError(error.code === 'auth/too-many-requests' ? signInThrottledMessage :
                error.message || 'Failed to log in. Please check your email and password.');
        } finally {
            authActionInProgress.current = false;
            setIsLoading(false);
            setLoadingStage('auth');
        }
    };

    const handleMicrosoftSignIn = async () => {
        if (authActionInProgress.current) return;
        authActionInProgress.current = true;
        setError(null);
        setSuccessMessage(null);
        setSignupRecoveryPath(null);
        setLoadingStage('auth');
        setIsLoading(true);
        
        try {
            const provider = new OAuthProvider('microsoft.com');
            provider.setCustomParameters({
                // Optional: you can specify tenant if needed
                // tenant: 'your-tenant-id'
            });
            
            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            setLoadingStage('dashboard');
            await verifyTherapistAndSetClaims(user);
        } 
        catch (e: any) {
            console.error('Error signing in with Microsoft', e);
            if (e.code === 'auth/too-many-requests') {
                setError(signInThrottledMessage);
            }
            else if (e.code === 'auth/popup-closed-by-user') {
                setError('Sign-in was cancelled.');
            } 
            else if (e.code === 'auth/popup-blocked') {
                setError('Popup was blocked by browser. Please allow popups and try again.');
            } 
            else if (e.message && e.message.includes('Access denied')) {
                setError(e.message);
            } 
            else {
                setError(e.message || 'Failed to sign in with Microsoft.');
            }
        } finally {
            authActionInProgress.current = false;
            setIsLoading(false);
            setLoadingStage('auth');
        }
    };

    const handleForgotPassword = async () => {
        if (authActionInProgress.current) return;
        setError(null);
        setSuccessMessage(null);
        setSignupRecoveryPath(null);

        if (!email) {
            setError('Please enter your email address first.');
            return;
        }

        authActionInProgress.current = true;
        setIsLoading(true);
        try {
            await sendPasswordResetEmail(auth, email.trim());
            setSuccessMessage('Password reset email sent. Please check your inbox.');
        } catch (error: any) {
            console.error('Error sending password reset email:', error);
            if (error.code === 'auth/too-many-requests') {
                setError('Firebase has temporarily limited password reset requests. Please wait before requesting another email.');
            } else if (error.code === 'auth/user-not-found') {
                setError('No account found with this email address.');
            } else if (error.code === 'auth/invalid-email') {
                setError('Please enter a valid email address.');
            } else {
                setError(error.message || 'Failed to send password reset email.');
            }
        } finally {
            authActionInProgress.current = false;
            setIsLoading(false);
        }
    };

    return (
        <main className='login-page'>
            <section className='login-marketing-intro' aria-labelledby='login-marketing-heading'>
                <img
                    className='login-brand-logo'
                    src={loginLogo}
                    alt='SuperPenguin'
                />

                <div className='login-hero'>
                    <h1 id='login-marketing-heading'>
                        Give every family the right support, between every session
                        <span className='login-hero__accent'>.</span>
                    </h1>
                    <p>
                        Manage your team and clients from one secure dashboard, while families use
                        SuperPenguin on their phones.
                    </p>

                    <div className='login-hero__actions'>
                        <a className='login-primary-cta' href='/plans'>
                            Start for free
                        </a>
                        <a className='login-secondary-cta' href='/plans'>
                            View plans
                            <ArrowForwardRoundedIcon aria-hidden='true' fontSize='small' />
                        </a>
                    </div>
                    <p className='login-hero__microcopy'>Free trial · No card required</p>
                </div>
            </section>

            <section className='login-auth-panel' aria-labelledby='login-heading'>
                <a className='login-help-link' href='mailto:support@superpenguin.com'>
                    <HelpOutlineIcon aria-hidden='true' fontSize='small' />
                    Need help?
                </a>

                <div className='login-auth-card'>
                    <h2 id='login-heading'>Welcome back</h2>
                    <p className='login-auth-card__intro'>Sign in to your clinic dashboard</p>

                    <form className='login-form' onSubmit={handleSubmit}>
                        <label htmlFor='login-email'>Work email</label>
                        <input
                            id='login-email'
                            type='email'
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder='you@clinic.com'
                            autoComplete='email'
                            disabled={isLoading}
                        />

                        <label htmlFor='login-password'>Password</label>
                        <input
                            id='login-password'
                            type='password'
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder='Enter your password'
                            autoComplete='current-password'
                            disabled={isLoading}
                        />

                        <button className='login-submit-button' type='submit' disabled={isLoading}>
                            {isLoading ? (loadingStage === 'dashboard' ? 'Preparing dashboard...' : 'Signing in...') : 'Sign in'}
                        </button>
                        <button
                            className='login-forgot-password'
                            type='button'
                            onClick={handleForgotPassword}
                            disabled={isLoading}
                        >
                            Forgot password?
                        </button>
                    </form>

                    <div className='login-feedback' aria-live='polite'>
                        {error && <p className='login-feedback__error' role='alert'>{error}</p>}
                        {signupRecoveryPath && (
                            <a className='login-signup-recovery' href={signupRecoveryPath}>
                                Continue setting up your clinic
                            </a>
                        )}
                        {successMessage && (
                            <p className='login-feedback__success' role='status'>{successMessage}</p>
                        )}
                    </div>

                    <div className='login-divider' aria-hidden='true'>
                        <span>OR</span>
                    </div>

                    <button
                        className='login-microsoft-button'
                        type='button'
                        onClick={handleMicrosoftSignIn}
                        disabled={isLoading}
                    >
                        <img
                            src='https://docs.microsoft.com/en-us/azure/active-directory/develop/media/howto-add-branding-in-azure-ad-apps/ms-symbollockup_mssymbol_19.png'
                            alt=''
                            aria-hidden='true'
                        />
                        {isLoading ? (loadingStage === 'dashboard' ? 'Preparing dashboard...' : 'Signing in...') : 'Sign in with Microsoft'}
                    </button>
                </div>

                <img className='login-penguin-backdrop' src={penguinBackdrop} alt='' aria-hidden='true' />
            </section>

            <section className='login-marketing-showcase' aria-label='SuperPenguin product highlights'>
                <div className='login-feature-list'>
                    <article className='login-feature'>
                        <GroupsOutlinedIcon aria-hidden='true' />
                        <div>
                            <h2>Client management and progress</h2>
                            <p>Track goals, notes and outcomes in one place.</p>
                        </div>
                    </article>
                    <article className='login-feature'>
                        <VpnKeyOutlinedIcon aria-hidden='true' />
                        <div>
                            <h2>Reusable family activation keys</h2>
                            <p>Invite families in seconds and keep access simple.</p>
                        </div>
                    </article>
                    <article className='login-feature'>
                        <GraphicEqIcon aria-hidden='true' />
                        <div>
                            <h2>Audio Scribe and AI reports</h2>
                            <p>Turn session notes into useful reports.</p>
                        </div>
                    </article>
                </div>

                <div className='login-dashboard-preview'>
                    <img src={dashboardPreview} alt='SuperPenguin dashboard preview' />
                </div>

                <footer className='login-footer'>© 2026 BeneTalk LTD. All rights reserved.</footer>
            </section>
        </main>
    );
};

export default Login;
